import API from "./API"
import Swal from "sweetalert2";


/**
 * 
 * @param {Object} obj 
 * @returns a deep copy of object
*/
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
export const deepCopy = (obj) => {
    let outObject, value, key
    if (typeof obj !== "object" || obj === null) {
        return obj // Return the value if obj is not an object
    }
    // Create an array or object to hold the values
    outObject = Array.isArray(obj) ? [] : {}
    for (key in obj) {
        value = obj[key]
        // Recursively (deep) copy for nested objects, including arrays
        outObject[key] = deepCopy(value)
    }
    return outObject
}

/**
 * 
 * @param {Object} obj 
 * @returns true if the object/array is empty or null, else false
 */
export const isEmpty = (obj) => {
    if (obj == null) return true;
    return Object.keys(obj).length === 0;
}


const SWAL_CONFIG = {
    confirmButtonText: 'Ok',
    confirmButtonColor: '#007b4e',
    icon: 'warning',
};

const showErrorAlert = (text, title = 'Oops!') =>
    Swal.fire({ ...SWAL_CONFIG, title, text });

/**
 * Polls the scan status of an uploaded file until it resolves.
 * @param {string} fileId - The file identifier to check.
 * @param {string} fileName - Used in error messages if the scan is rejected.
 * @returns {Promise<boolean|null>} true if clean, false if rejected, null on error.
 */
const pollScanStatus = async (fileId, fileName) => {
    while (true) {
        try {
            const { data: status } = await API.scanStatus({ url: fileId });

            switch (status) {
                case 'clean':
                    return true;

                case 'rejected':
                    showErrorAlert(`"${fileName}" failed the malware scan and was rejected.`, 'Upload Rejected');
                    return false;

                default:
                    await sleep(5_000);
            }
        } catch {
            showErrorAlert('An error occurred while scanning the file. Please try again later.');
            return null;
        }
    }
};

export const uploadAndScan = async (list) => {
    try {
        const uploadedUrls = await Promise.all(
            list.map(async (file) => {
                let res
                if (['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(file.type)) {
                    res = await API.uploadFile({ 'file': file.data });
                } else {
                    res = await API.uploadImage({ image: file.data });
                }
                const scanPassed = await pollScanStatus(res.data, file.name);
                return scanPassed ? res.data : null;
            })
        );

        return uploadedUrls.filter(Boolean)
    } catch (err) {
        console.error(err);
        if (err.message !== 'cancelling') {
            const message = err.response?.data?.error || 'Please try again later.';
            Swal.fire({
                title: 'Error.',
                text: message,
                icon: 'warning',
                confirmButtonText: 'Ok',
                confirmButtonColor: '#007b4e',
            });
        }
    }

}

export const withRetry504 = async (fn, { retries = 3, delayMs = 500 } = {}) => {
    let attempt = 0;
    while (true) {
        try {
            return await fn();
        } catch (e) {
            const status = e?.response?.status ?? e?.status;
            const isLastAttempt = attempt >= retries;

            if (status !== 504 || isLastAttempt) {
                throw e;
            }

            attempt++;
            const backoff = delayMs * Math.pow(2, attempt - 1); // 500ms, 1000ms, 2000ms...
            console.log(`504 received, retrying attempt ${attempt}/${retries} after ${backoff}ms`);
            await sleep(backoff);
        }
    }
};
